import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Injector, Input, OnDestroy, Output } from '@angular/core';
import { NgIf } from '@angular/common';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { TlkModalWindowService } from '@itero/ui-toolkit-angular/modal-window';
import { PopUpActions } from '@shared/models/enums/popup-modal-actions.enum';
import { ControlValueAccessorDirective } from '@shared/base-classes/control-value-accessor.directive';
import { TilesComponent, Tile } from '@shared/components/tiles/tiles.component';
import { Procedure } from '@shared/models/procedure';
import { BiLoggerService } from '@core/services/logger/bi-logger.service';
import { BiAction } from '@core/services/logger/interfaces/bi.enums';
import { FLOW_RX_STEP_ATTR } from '@modules/rx-intuitive/models/flow-rx-step';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { ConfirmationDialogComponent, ConfimationDialogData } from '../toolkit-confirmation-dialog/confirmation-dialog.component';
import { TranslateModule } from '@ngx-translate/core';

export interface RxProcedureTile extends Procedure {
	Icon?: string;
}

type ProcedureId = RxProcedureTile['Id'];

@Component({
	selector: 'rx-procedure-tiles',
	templateUrl: './rx-procedure-tiles.component.html',
	styleUrls: ['./rx-procedure-tiles.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [NgIf, FormsModule, TilesComponent, TranslateModule],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: RxProcedureTilesComponent,
			multi: true
		}
	]
})
export class RxProcedureTilesComponent extends ControlValueAccessorDirective<ProcedureId | null> implements OnDestroy {
	@Input() title = 'FlexibleRxProcedure.ProcedureTitle';

	@Input() procedures: RxProcedureTile[];
	@Input() selectedProcedureId: ProcedureId | null;

	@Input() promptOnChange = true;

	@Output() procedureChanged = new EventEmitter<ProcedureId | null>();
	@Output() changeProcedureCancelled = new EventEmitter<void>();

	isDisabled = false;
	private dialogCloseSubscription?: Subscription;

	constructor(
		private readonly changeDetectorRef: ChangeDetectorRef,
		private readonly modalService: TlkModalWindowService,
		private readonly biLogger: BiLoggerService,
		private readonly shellQuery: ShellQuery,
		injector: Injector
	) {
		super(injector);
	}

	get tiles(): Tile[] {
		if (!this.procedures || this.procedures.length === 0) {
			return [];
		}

		return this.procedures.map(procedure => ({
			id: procedure.Id,
			title: procedure.Name,
			iconUrl: procedure.Icon
		}));
	}

	tileClicked(tile: Tile): void {
		if (this.isDisabled) {
			return;
		}
		if (
			this.selectedProcedureId !== tile.id &&
			this.selectedProcedureId !== null &&
			this.selectedProcedureId !== undefined &&
			this.promptOnChange
		) {
			this.promptProcedureChange(tile);
		} else {
			this.changeProcedure(tile);
		}
	}

	ngOnDestroy(): void {
		this.dialogCloseSubscription?.unsubscribe();
	}

	writeValue(value: ProcedureId | null): void {
		this.selectedProcedureId = value ?? null;
		this.changeDetectorRef.markForCheck();
	}

	registerOnChange(fn: (value: ProcedureId | null) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.isDisabled = isDisabled;
		this.changeDetectorRef.markForCheck();
	}

	private onChange: (value: ProcedureId | null) => void = () => {};
	private onTouched: () => void = () => {};

	private promptProcedureChange(tile: Tile): void {
		if (this.dialogCloseSubscription) {
			this.dialogCloseSubscription.unsubscribe();
			this.modalService.closeAll();
		}

		const dialogRef = this.modalService.open<PopUpActions, ConfimationDialogData, ConfirmationDialogComponent>(
			ConfirmationDialogComponent,
			{
				data: {
					titleTranslationKey: 'FlexibleRxProcedure.ChangeProcedureTitle',
					contentTranslationKey: 'FlexibleRxProcedure.ChangeProcedureMessage',
					confirmTranslationKey: 'FlexibleRxProcedure.ChangeProcedureOkButton',
					cancelTranslationKey: 'FlexibleRxProcedure.ChangeProcedureCancelButton'
				},
				panelClass: 'change-confimation-dialog'
			}
		);

		this.dialogCloseSubscription = dialogRef.closed.pipe(take(1)).subscribe(result => {
			if (result === PopUpActions.Ok) {
				this.changeProcedure(tile);
			}
			else{
				this.changeProcedureCancelled.emit();
			}
		});
	}

	private changeProcedure(tile: Tile): void {
		this.onChange(tile.id);
		this.onTouched();
		this.procedureChanged.emit(tile.id);
		void this.biLogger.reporter?.trackUserAction({
			objectName: 'procedure-selector',
			action: BiAction.Select,
			objectType: 'listbox',
			selectedValue: tile.title,
			customAttributes: { [FLOW_RX_STEP_ATTR]: this.shellQuery.flowRxStep ?? '' }
		});
	}
}
